import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { SERVICE_CATALOG as SERVICE_PAGES, SERVICE_GROUPS as SERVICE_PAGE_GROUPS } from "@/data/serviceCatalog";
import { IMAGES } from "@/data/site";
import { mediaThumb } from "@/lib/media";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useSEO, breadcrumbLd, organizationLd } from "@/lib/seo";

export default function Services() {
  useSEO({
    title: "Construction and Digital Services | MRE Developments",
    description:
      "Every service under one roof: new homes, renovations, additions, concrete, framing, roofing, electrical, plumbing, tiling, painting and more across Calgary. Free quote.",
    path: "/services",
    image: SERVICE_PAGES[0]?.hero || IMAGES.buildPanel,
    jsonLd: [
      organizationLd(),
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: SERVICE_PAGES.map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: s.name,
          url: `https://mredevelopments.com/services/${s.slug}`,
        })),
      },
    ],
  });

  return (
    <div data-testid="services-index">
      <PageHero
        overline="Services"
        title="We do everything."
        intro="From the foundation to the finish. Every trade a Calgary home or business could need — one accountable partner, honest pricing, no surprises."
        image={SERVICE_PAGES[0]?.hero || IMAGES.buildPanel}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]}
      />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
        <div className="border border-border p-6 mb-12 flex flex-wrap items-center justify-between gap-5">
          <p className="font-display uppercase text-2xl">Planning a bigger project?</p>
          <div className="flex flex-wrap gap-5 text-primary">
            <Link to="/start/roofing" className="underline underline-offset-4">Plan a roofing project</Link>
            <Link to="/start/full-home-renovations" className="underline underline-offset-4">Plan a full-home renovation</Link>
            <Link to="/start/bathroom-renovations" className="underline underline-offset-4">Plan a bathroom renovation</Link>
          </div>
        </div>
        {SERVICE_PAGE_GROUPS.filter((g) => SERVICE_PAGES.some((s) => s.group === g)).map((group) => (
          <div key={group} className="mb-16">
            <SectionHeading overline={group} title={group} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border mt-8">
              {SERVICE_PAGES.filter((s) => s.group === group).map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`services-link-${s.slug}`}
                  className="group bg-background hover:bg-secondary/40 transition-colors overflow-hidden"
                >
                  {s.hero && (
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={mediaThumb(s.hero)}
                        alt={s.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display uppercase tracking-tight text-2xl group-hover:text-primary transition-colors">{s.name}</h3>
                      <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-1" />
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{s.intro}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      <CTASection title="Whatever you need built." />
    </div>
  );
}
